/**
 * Os LEADS do painel: quem chamou no WhatsApp e não escolheu departamento.
 *
 * Pedido da Gisele (19/10/2026): "uma área para visualizar e também conseguir
 * entrar nessas conversas onde o cliente (via WhatsApp) chama e não interage
 * com os campos que enviamos para eles".
 *
 * ## Como essa conversa existe hoje
 *
 * O cliente escreve pela primeira vez, a sala nasce (`status 1`, sem
 * operador), e nós mandamos o menu de departamentos. Enquanto ele não escolhe,
 * a sala fica **sem `service_group_id`** -- e o menu é remandado a cada
 * mensagem nova dele. Escolhido o departamento, a sala é atribuída (ou entra
 * na fila COM departamento), e deixa de ser lead.
 *
 * Até aqui essas conversas caíam na fila "aguardando" misturadas com todo o
 * resto, sem nada que as distinguisse. Era o que a Gisele não conseguia ver.
 *
 * ## Por que o sinal é o `whatsapp_number_id`, e não "é de WhatsApp"
 *
 * Porque existem DOIS caminhos de WhatsApp no banco:
 *
 * - a **integração própria** (Meta), que grava `whatsapp_number_id` -- é a que
 *   manda o menu, e só nela "sem departamento" significa "não escolheu";
 * - o caminho **antigo** (Smclick), que grava `whatsapp_instance_id` e **nunca
 *   preenche departamento nenhum**.
 *
 * Usar "é de WhatsApp e está sem departamento" varreria para a área de leads
 * todo o tráfego do número real de hoje, que não tem menu nenhum para ignorar.
 * O corte por `whatsapp_number_id` é o que separa uma coisa da outra.
 *
 * ## Entrar no lead assume a conversa
 *
 * Decisão do dono do produto: é o mesmo "pegar" da fila -- a sala passa a ter
 * operador. Não há departamento a gravar: o encerramento por inatividade já
 * cai no departamento do RESPONSÁVEL quando a sala não tem o seu
 * (`EncerrarChatsInativos::departamentoDaSala`), então assumir basta.
 */

/** O id do número próprio (Meta) pelo qual a conversa entrou, se houver. */
const numeroProprio = (conversa) => conversa?.whatsapp_number_id ?? null;

/**
 * Esta conversa da fila é um lead?
 *
 * @param {object} conversa a sala como vem de `/rooms/waiting`
 */
export const ehLead = (conversa) => {
    if (!conversa) return false;

    const numero = numeroProprio(conversa);

    if (numero === null || numero === undefined || numero === '') return false;

    const departamento = conversa.service_group_id;

    // Zero não é id válido de departamento, e a string vazia é o que uma
    // serialização torta devolve: os três significam "não escolheu".
    return departamento === null || departamento === undefined || departamento === '' || departamento === 0;
};

/**
 * Separa a fila em leads e o resto, mantendo a ordem de cada lista.
 *
 * Duas listas e não um sinalizador por linha: os dois cartões são lugares
 * diferentes da tela, e o lead SAI da fila -- deixar nos dois faria a mesma
 * conversa ser contada duas vezes, e o supervisor tentaria atender duas.
 *
 * @param {Array} conversas
 * @returns {{leads: Array, fila: Array}}
 */
export const separarLeads = (conversas) => {
    const lista = (Array.isArray(conversas) ? conversas : []).filter(Boolean);

    return {
        leads: lista.filter(ehLead),
        fila: lista.filter((c) => !ehLead(c)),
    };
};
